import * as React from "react";
import {useEffect, useState} from 'react'
import cx from "classnames";

import Typography from "@mui/material/Typography";
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { alpha } from '@mui/material/styles';
import { ExpandMore as ExpandMoreIcon } from '@vedmarket/ui-kit';
import { ExpandLess as ExpandLessIcon } from '@vedmarket/ui-kit';
import { Edit as EditIcon } from '@vedmarket/ui-kit';
import { CheckCircle as CheckCircleIcon } from '@vedmarket/ui-kit';

import COLORS from 'styles/colors.scss'
import './template.scss'

const Template = (props: CalculatorStepTemplateProps) => {

  const shouldShowProgressIcon = (!props.isDisabled || props.inProgress) && !props.isCompleted;

  const [ isExpandedByDefault, setIsExpandedByDefault ] = useState(props.isExpanded);

  useEffect(() => {
    setIsExpandedByDefault(prev => {
      if (prev !== props.isExpanded) {
        return props.isExpanded;
      }
      return prev;
    });

  }, [ props.isExpanded ]);

  const handleChange = (event: React.SyntheticEvent, expanded: boolean) => {
    if (props.isExpansionControlled) {
      props.onExpandedChange?.(expanded)
      return
    }

    setIsExpandedByDefault(expanded)
  }

  const isExpanded = props.isExpansionControlled ? props.isExpanded : isExpandedByDefault
  const shouldShowCollapseIcon = isExpanded && !props.isCompleted
  const ExpandIcon = shouldShowCollapseIcon ? ExpandLessIcon : ExpandMoreIcon

  return (
    <Accordion
      className='calculator-step'
      data-calculator-step={props.id}
      disabled={props.isDisabled}
      expanded={isExpanded}
      onChange={handleChange}
      TransitionProps={{ onExited: props.onTransitionExited }}
    >
      <AccordionSummary
        expandIcon={<ExpandIcon style={{
          width: '24px',
          height: '24px',
        }}/>}
        aria-controls={`step-${props.id}`}
        id={`step-${props.id}`}
        sx={{
          padding: '24px',
          minHeight: '80px',
          '&.Mui-expanded': {
            minHeight: '80px',
            borderBottom: 0,
            boxShadow: `inset 0 -1px ${alpha(COLORS.BLACK_DARK, 0.12)}`,
          },
          '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded': {
            margin: 0,
          },
          '& .MuiAccordionSummary-expandIconWrapper, & .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
            transform: 'none',
          },
          '&.Mui-disabled': {
            padding: '24px 64px 24px 24px',
          }
        }}
      >
        <div className='calculator-step__header'>
          <Typography
            variant='h5'
            className={cx('calculator-step__title', {
              'calculator-step__title_disabled': props.isDisabled,
            })}
            sx={{
              fontWeight: 700,
              lineHeight: 1.28,
              letterSpacing: 0,
              color: COLORS.BLACK_DARK,
            }}
          >
            {props.isCompleted && <CheckCircleIcon style={{
              color: COLORS.GREEN,
              width: '24px',
              height: '24px',
              flexShrink: 0,
            }} />}
            {shouldShowProgressIcon && <EditIcon style={{
              color: COLORS.LIGHT_TEXT_DISABLED_OPACITY,
              width: '24px',
              height: '24px',
              flexShrink: 0,
            }} />}
            {props.header.name}
          </Typography>
          <Typography component='span' variant='caption' sx={{
            color: COLORS.BLACK_DARK,
            backgroundColor: alpha(COLORS.BLACK_DARK, 0.08),
            borderRadius: '16px',
            padding: '7px 10px',
            fontSize: '12px',
            lineHeight: '18px',
            letterSpacing: 0,
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}>
            {props.header.text}
          </Typography>
        </div>
      </AccordionSummary>
      <AccordionDetails>
        {props.children}
      </AccordionDetails>
    </Accordion>
  )
}

export default Template
